import { Decimal, istanbulOffsetMinutes, Money } from "@station/core";
import { isBasketCampaign, } from "./campaign.js";
import { defaultUnitPriceScale, lineGross, } from "./types.js";
/** Yerel (Europe/Istanbul) hafta günü: 0 = Pazar … 6 = Cumartesi — `campaignSchema.weekdays` ile aynı düzen. */
export function istanbulWeekday(at) {
    const local = new Date(at.getTime() + istanbulOffsetMinutes(at) * 60_000);
    return local.getUTCDay();
}
/** Yerel (Europe/Istanbul) saat, "HH:MM". */
export function istanbulLocalTime(at) {
    const local = new Date(at.getTime() + istanbulOffsetMinutes(at) * 60_000);
    return `${String(local.getUTCHours()).padStart(2, "0")}:${String(local.getUTCMinutes()).padStart(2, "0")}`;
}
/**
 * Zaman penceresi denetimi. Pencere yarı açıktır: `[from, to)`.
 *  - İkisi de boş → gün boyu. `from === to` → gün boyu (sıfır uzunlukta pencere anlamsızdır).
 *  - `to < from` → gece yarısını aşar: `t >= from || t < to`.
 */
export function withinTimeWindow(localTime, from, to) {
    if (!from && !to)
        return true;
    if (from && !to)
        return localTime >= from;
    if (!from && to)
        return localTime < to;
    if (from === to)
        return true;
    return from < to ? localTime >= from && localTime < to : localTime >= from || localTime < to;
}
/**
 * Müşteri türü eşleşmesi: `IDENTIFIED` kampanyası filo müşterisini de kapsar (filo tanınmış müşteridir).
 * `LOYALTY_TIER` türden değil SEVİYEDEN karar verir → burada `false`; bkz. `loyaltyTierMatches`.
 */
export function customerKindMatches(required, actual) {
    switch (required) {
        case "ANY":
            return true;
        case "IDENTIFIED":
            return actual === "IDENTIFIED" || actual === "FLEET";
        case "FLEET":
            return actual === "FLEET";
        case "LOYALTY_TIER":
            return false;
    }
}
/** Sadakat seviyesi eşleşmesi: seviye kodu listede BİREBİR (büyük / küçük harf duyarlı) bulunmalıdır. */
export function loyaltyTierMatches(tiers, tier) {
    if (tier === null || tier === undefined || tier.trim() === "")
        return false;
    return tiers.includes(tier.trim());
}
function scopeMatches(campaign, line) {
    switch (campaign.scopeKind) {
        case "PRODUCT":
            return campaign.scopeIds.includes(line.productId);
        case "CATEGORY":
            return line.categoryId != null && campaign.scopeIds.includes(line.categoryId);
        case "BUSINESS_UNIT":
            return line.businessUnit != null && campaign.scopeIds.includes(line.businessUnit);
    }
}
/** Kampanyanın BAĞLAM (satırdan bağımsız) koşulları: etkinlik, istasyon, tarih aralığı, gün, saat, müşteri. */
export function campaignActiveAt(campaign, context) {
    if (!campaign.isActive)
        return false;
    if (campaign.stationIds.length > 0 && !campaign.stationIds.includes(context.stationId))
        return false;
    const at = context.at.getTime();
    if (at < Date.parse(campaign.validFrom))
        return false;
    if (campaign.validTo && at >= Date.parse(campaign.validTo))
        return false;
    if (campaign.weekdays.length > 0 && !campaign.weekdays.includes(istanbulWeekday(context.at)))
        return false;
    if (!withinTimeWindow(istanbulLocalTime(context.at), campaign.timeFrom, campaign.timeTo))
        return false;
    if (campaign.requiresCoupon && !(context.couponCampaignIds ?? []).includes(campaign.id))
        return false;
    if (campaign.crossCondition?.kind === "CONTEXT_FLAG" &&
        !(context.flags ?? []).includes(campaign.crossCondition.flag))
        return false;
    if (campaign.customerKind === "LOYALTY_TIER")
        return loyaltyTierMatches(campaign.loyaltyTiers ?? [], context.loyaltyTier);
    return customerKindMatches(campaign.customerKind, context.customerKind);
}
/**
 * Sepet düzeyi çapraz koşul (`BASKET_LINE_KIND_MIN_QUANTITY`): sepetteki o türden satırların toplam miktarı eşiği
 * karşılamalı. Bağlam bayrağı (`CONTEXT_FLAG`) `campaignActiveAt`'te sınanır; koşulsuz kampanya her zaman sağlar.
 */
export function crossConditionMet(campaign, lines) {
    const condition = campaign.crossCondition;
    if (!condition || condition.kind !== "BASKET_LINE_KIND_MIN_QUANTITY")
        return true;
    const total = Decimal.sum(lines.filter((line) => line.kind === condition.lineKind).map((line) => line.quantity));
    return total.gte(condition.minQuantity);
}
/**
 * Satır koşulları: kapsam, asgari miktar, asgari satır tutarı. `BUY_X_PAY_Y` için miktar ≥ X de koşuldur: X'e
 * ulaşmayan satır kampanyayla EŞLEŞMEZ ve sıradaki (daha düşük öncelikli) kampanyaya açık kalır.
 */
export function campaignMatchesLine(campaign, line, gross) {
    if (line.campaignsDisabled)
        return false;
    if (!scopeMatches(campaign, line))
        return false;
    if (campaign.minQuantity && Decimal.from(line.quantity).lt(campaign.minQuantity))
        return false;
    if (campaign.minLineGross && gross.lt(Money.fromMinor(campaign.minLineGross)))
        return false;
    if (campaign.effectType === "BUY_X_PAY_Y") {
        if (!campaign.buyQuantity || !campaign.payQuantity)
            return false;
        if (Decimal.from(line.quantity).lt(campaign.buyQuantity))
            return false;
    }
    return true;
}
/**
 * X al Y öde: bedava birim sayısı `⌊miktar / X⌋ × (X − Y)`. Kesirli miktarda (litre) taban doğal davranır:
 * 3 al 2 öde, 7,5 L → ⌊2,5⌋ = 2 set → 2 L bedava.
 */
export function buyXPayYFreeUnits(quantity, buyQuantity, payQuantity) {
    const sets = Decimal.from(quantity).div(buyQuantity).floor(0);
    if (sets.isNegative())
        return Decimal.ZERO;
    return sets.mul(buyQuantity - payQuantity);
}
/**
 * Etkinin kuruş karşılığı. Sonuç `[0, gross]` aralığına KIRPILIR; sıfırsa kampanya uygulanmamış sayılır.
 * `FIXED_UNIT_PRICE` satırın birim fiyat ÖLÇEĞİNİ kullanır (akaryakıtta ₺/L, markette kuruş).
 */
export function campaignDiscountAmount(campaign, line, gross, scale) {
    let raw;
    switch (campaign.effectType) {
        case "PERCENT_OFF":
            raw = gross.percentage(campaign.effectValue);
            break;
        case "AMOUNT_OFF":
            // Birim başına KURUŞ indirimi × miktar.
            raw = Money.fromMinor(Decimal.from(campaign.effectValue).mul(line.quantity).round(0).toBigInt());
            break;
        case "FIXED_UNIT_PRICE":
            raw = gross.sub(lineGross(campaign.effectValue, line.quantity, scale));
            break;
        case "BUY_X_PAY_Y": {
            // Bedava birimlerin değeri SATIR BRÜTÜNDEN orantıyla: brüt × bedava / miktar (KDV dâhil, ROUND_HALF_UP).
            // Bedava ≤ miktar olduğundan sonuç brütü aşamaz; birim fiyatla çarpılsaydı kuruş yuvarlaması aşabilirdi.
            if (!campaign.buyQuantity || !campaign.payQuantity || !Decimal.from(line.quantity).isPositive()) {
                raw = Money.ZERO;
                break;
            }
            const free = buyXPayYFreeUnits(line.quantity, campaign.buyQuantity, campaign.payQuantity);
            raw = Money.fromMinor(Decimal.from(gross.minor).mul(free).div(line.quantity).round(0).toBigInt());
            break;
        }
    }
    if (raw.isNegative())
        return Money.ZERO;
    return raw.gt(gross) ? gross : raw;
}
/** Kampanyaları deterministik sıraya dizer: `priority` artan, eşitlikte `code` alfabetik. */
export function orderCampaigns(campaigns) {
    return [...campaigns].sort((a, b) => a.priority - b.priority || (a.code < b.code ? -1 : a.code > b.code ? 1 : 0));
}
/**
 * Sepet satırlarına kampanyaları uygular ve indirim satırlarını döndürür (`pos-local-api` §3 `discounts[]`).
 * Sıfır tutarlı indirim ÜRETİLMEZ; çıktı satır sırasındadır (bir satırın en çok bir indirimi olur).
 *
 * Çakışma kuralı (ADR 0016 §17 — BAĞLAYICI):
 *  1. SATIR kampanyaları (`minBasketGross` boş) öncelik sırasıyla denenir; her satırı eşleşen İLK kampanya alır.
 *  2. Sepet tutarı = `Σ satır brütü − Σ 1. aşama indirimleri` (tüm satırlar; kilitli / kapsam dışı satırlar dâhil).
 *  3. SEPET kampanyaları (`minBasketGross` dolu) öncelik sırasıyla: eşik `sepet tutarı ≥ minBasketGross` ise kampanya
 *     yalnızca HENÜZ KAMPANYA ALMAMIŞ ve kapsamına giren satırlara uygulanır; sonra sepet tutarı bu kampanyanın
 *     indirimleri kadar düşer ve sıradaki sepet kampanyasının eşiği YENİ tutarla sınanır.
 *
 * Örnek: sepet 150 ₺ kola (3 al 2 öde → −50 ₺) + 120 ₺ çikolata; sepet kampanyası "200 ₺ üzeri markete %10".
 * Satır aşamasından sonra sepet 220 ₺ ≥ 200 ₺ → %10 YALNIZCA çikolataya (−12 ₺); kola zaten kampanya aldı.
 */
export function applyCampaigns(lines, campaigns, context) {
    const eligible = orderCampaigns(campaigns).filter((campaign) => campaignActiveAt(campaign, context) && crossConditionMet(campaign, lines));
    if (eligible.length === 0)
        return [];
    const priced = lines.map((line) => {
        const scale = line.unitPriceScale ?? defaultUnitPriceScale(line.kind);
        return { line, scale, gross: lineGross(line.unitPrice, line.quantity, scale) };
    });
    /** Satır KONUMU → verilen indirim (satır numarası yinelense bile konum tekildir). */
    const byIndex = new Map();
    /** Bir kampanyayla eşleşmiş (etkisi sıfır olsa da) satırlar: başka kampanya almaz. */
    const claimed = new Set();
    const tryCampaign = (campaign) => {
        let total = Money.ZERO;
        priced.forEach(({ line, scale, gross }, index) => {
            if (claimed.has(index) || !gross.isPositive())
                return;
            if (!campaignMatchesLine(campaign, line, gross))
                return;
            claimed.add(index);
            const amount = campaignDiscountAmount(campaign, line, gross, scale);
            if (!amount.isPositive())
                return;
            byIndex.set(index, {
                lineNo: line.lineNo,
                campaignId: campaign.id,
                amount: amount.toString(),
                reason: campaign.code,
            });
            total = total.add(amount);
        });
        return total;
    };
    // 1. aşama — satır kampanyaları.
    let basketNet = Money.sum(priced.map(({ gross }) => gross));
    for (const campaign of eligible) {
        if (isBasketCampaign(campaign))
            continue;
        basketNet = basketNet.sub(tryCampaign(campaign));
    }
    // 2. aşama — sepet kampanyaları (eşik her kampanyadan sonra YENİDEN hesaplanır).
    for (const campaign of eligible) {
        if (!isBasketCampaign(campaign))
            continue;
        if (basketNet.lt(Money.fromMinor(campaign.minBasketGross)))
            continue;
        basketNet = basketNet.sub(tryCampaign(campaign));
    }
    return [...byIndex.entries()].sort(([a], [b]) => a - b).map(([, discount]) => discount);
}
//# sourceMappingURL=apply.js.map